/** @jsx jsx */
import { Spacer } from "@cd2/components"
import { Form2 } from "@cd2/forms/build/es6"
import { H3, P } from "@cd2/text"
import { css, jsx } from "@emotion/core"
import React, { useState } from "react"
import { useLocation } from "react-router-dom"
import { useSiteAlerts } from "./hooks/useSiteAlerts"
import { FileboyImageOrLocalBackgroundImage } from "./interfaceParts/FileboyImageOrLocal"
import { TextGroupLarge } from "./interfaceParts/Title"
import { useApi } from "./providers/ApiProvider"
import { MultiButton } from "./interfaceParts/MultiButton"
import { messageFromError } from "./utils/error"

interface IProps {
  signedIn?: boolean
  children: React.ReactNode
  onDidCatch?(error: Error | null, info: any): void
  renderOnError?(error: Error | null, info: any): React.ReactNode
}

const siteLoadedAt = new Date()

const ignoreErrors = [
  // Happens after a deploy; we already hard-reload when this is detected.
  "dynamically imported module",
  // Generic client network failure; nothing actionable in reports.
  "Network error: Failed to fetch",
] as const

function buildErrorReportContext() {
  const globalState: any = (window as any).globalState
  const userEmail =
    globalState?.user?.contactEmail ?? globalState?.user?.skeem_email ?? globalState?.user?.email

  return {
    siteLoadedAt: siteLoadedAt?.toISOString?.() ?? null,
    occurredAt: new Date().toISOString(),
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    user: globalState?.user
      ? {
          id: globalState.user.id,
          name: globalState.user.name,
          email: userEmail,
        }
      : null,
    company:
      globalState?.company || globalState?.companyId
        ? {
            id: globalState?.company?.id ?? globalState?.companyId ?? undefined,
            name: globalState?.company?.name ?? undefined,
          }
        : null,
  }
}

function shouldSkipErrorBoundaryAutoReport(error: Error | null): boolean {
  const haystack = messageFromError(error)
  return ignoreErrors.some(frag => haystack.includes(frag))
}

interface IPropsRaw {
  children: React.ReactNode
  onDidCatch?(error: Error | null, info: any): void
  renderOnError(error: Error | null, info: any): React.ReactNode
}

export function ErrorBoundaryRaw({ children, renderOnError, onDidCatch }: IPropsRaw) {
  const location = useLocation()

  function handleDidCatch(error: Error | null, info: any) {
    onDidCatch?.(error, info)
    console.error(error, info)
  }

  return (
    <ErrorBoundaryClass
      pathname={location.pathname}
      onDidCatch={handleDidCatch}
      renderOnError={renderOnError}
    >
      {children}
    </ErrorBoundaryClass>
  )
}

export function ErrorBoundary({ children, renderOnError }: IProps) {
  const location = useLocation()

  return (
    <ErrorBoundaryClass pathname={location.pathname} renderOnError={renderOnError}>
      {children}
    </ErrorBoundaryClass>
  )
}

class ErrorBoundaryClass extends React.Component<{ pathname: string } & IProps> {
  state = {
    hasError: false,
    error: null as Error | null,
    info: undefined as undefined | Record<string, unknown>,
  }

  private didAutoReport = false

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(
    error: Error | null,
    info: {
      componentStack: string
    },
  ) {
    console.error("ErrorBoundary caught an error:", error, info)

    if (!this.didAutoReport) {
      this.didAutoReport = true
      if (!shouldSkipErrorBoundaryAutoReport(error)) {
        try {
          void window.api?.sendErrorPageReport({
            reason: "ErrorBoundary caught an error (auto report)",
            url: window.location.href,
            error: messageFromError(error),
            context: {
              ...buildErrorReportContext(),
              componentStack: info?.componentStack ?? null,
            },
          })
        } catch (e) {
          console.error("Failed to auto-send error report", e)
        }
      }
    }

    if (
      error instanceof Error &&
      error.message.startsWith("Failed to fetch dynamically imported module")
    ) {
      // This is likely caused by a deploy
      const triedRefresh = window.localStorage.getItem("triedRefreshAt")
      if (!triedRefresh || Date.now() - parseInt(triedRefresh) > 1000 * 60 * 1) {
        window.localStorage.setItem("triedRefreshAt", Date.now().toString())
        window.location.reload()
      }
    }

    this.props.onDidCatch?.(error, info)
    this.setState({ error, info })
  }

  componentDidUpdate(prevProps: { pathname: string } & IProps) {
    if (this.state.hasError && prevProps.pathname !== this.props.pathname) {
      this.setState({ hasError: false, error: null })
    }
  }

  onClearError = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    const { hasError } = this.state
    return hasError ? (
      this.props.renderOnError ? (
        this.props.renderOnError(this.state.error, this.state.info)
      ) : (
        <ErrorPage error={this.state.error} />
      )
    ) : (
      this.props.children
    )
  }
}

function ErrorPage({ error }: { error?: any }) {
  const alert = useSiteAlerts()
  const [submitted, setSubmitted] = useState(false)
  const api = useApi()

  function handleDashboardClick() {
    window.location.href = "/"
    window.location.reload()
  }

  async function handleSendFeedback({ reason }: { reason: string }) {
    try {
      // Must stringify Error before POST — JSON.stringify(Error) is "{}".
      await api.sendErrorPageReport({
        reason,
        url: window.location.href,
        error: messageFromError(error),
        context: buildErrorReportContext(),
      })
      alert.genericSuccess("Thank you for your feedback!")
      setSubmitted(true)
    } catch (e) {
      console.error("Error sending report", e)
      alert.genericError("Unable to send report. Please contact support.")
    }
  }

  return (
    <div
      css={css`
        text-align: center;
        max-width: 700px;
        margin: 0 auto;
        padding: 32px 32px 200px;
      `}
    >
      <FileboyImageOrLocalBackgroundImage
        image={"squid"}
        css={css`
          height: 280px;
        `}
        contain
      />
      <Spacer />
      <TextGroupLarge
        theme={{ textBlock: { align: "center" } }}
        heading="Something has gone wrong."
        body={
          <P color="primary">
            Please try again, if this issue still persists you can contact support to let us know or
            fill in the form below with what you were doing when this error occurred.
          </P>
        }
      />

      <Spacer size={32} />
      {submitted ? (
        <div>
          <H3>Thank you for your report.</H3>
          <P>We will look into this error as soon as possible.</P>
          <P>A reply will not be forthcoming.</P>
        </div>
      ) : (
        <div style={{ textAlign: "left" }}>
          <Form2.Form onSubmit={handleSendFeedback}>
            <Form2.TextArea
              field="reason"
              label="What you were doing when this error occurred?"
              validations={{ presence: true }}
            />
            <div>
              <Form2.Submit />
            </div>
          </Form2.Form>
        </div>
      )}
      <Spacer size={32} />
      <MultiButton
        buttons={[
          {
            children: "To the Dashboard",
            onClick: handleDashboardClick,
            leftIcon: "arrow-left",
            buttonStyle: "outline",
          },
        ]}
        justifyContent={"center"}
      />
      <div
        css={css`
          font-size: 12px;
          background: #f5f5f5;
          padding: 16px;
          margin-top: 16px;
          text-align: left;
          border-radius: 4px;
          color: #666;
        `}
      >
        <div>
          Site Loaded At: <b>{siteLoadedAt.toISOString()}</b>
        </div>
        <RenderError error={error} />
      </div>
    </div>
  )
}

function RenderError({ error }: { error?: any }) {
  if (!error) {
    return <div>{`"null"`}</div>
  }
  if (error instanceof Error) {
    return (
      <div>
        <pre>{error.message}</pre>
        <pre
          css={css`
            white-space: pre-wrap;
          `}
        >
          {error.stack}
        </pre>
      </div>
    )
  }
  // if we can json stringify it, do so
  if (typeof error === "object") {
    try {
      return (
        <pre
          css={css`
            white-space: pre-wrap;
          `}
        >
          {JSON.stringify(error, null, 2)}
        </pre>
      )
    } catch (e) {
      return <pre>{error.toString()}</pre>
    }
  }
  return <pre>{error.toString()}</pre>
}
